import type { ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  Dialog,
  DialogPortal,
  DialogClose,
} from '../../components/ui/dialog.lib'
import {
  DialogOverlay,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog'
import { PexIcon } from '../../components/icons/PexIcon'
import { useT } from '../../i18n'
import { reportCopy } from './copy'
export default function ReportDialog({
  open,
  onRequestClose,
  onCloseAutoFocus,
  tab,
  setTab,
  canAdmin,
  children,
  footer,
}: {
  open: boolean
  onRequestClose: () => void
  onCloseAutoFocus: (event: Event) => void
  tab: 'new' | 'own' | 'admin'
  setTab: (tab: 'new' | 'own' | 'admin') => void
  canAdmin: boolean
  children: ReactNode
  footer: ReactNode
}) {
  const t = useT(reportCopy)
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onRequestClose()
      }}
    >
      <DialogPortal>
        <DialogOverlay
          data-problem-report-overlay
          className="motion-reduce:animate-none"
        />
        <DialogPrimitive.Content
          data-problem-report-overlay
          onCloseAutoFocus={onCloseAutoFocus}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-1.5rem)] -translate-x-1/2 -translate-y-1/2 border shadow-lg max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] max-sm:inset-0 max-sm:h-dvh max-sm:w-dvw max-sm:max-h-none max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 flex flex-col gap-0 overflow-hidden border-[var(--color-border)] bg-[var(--color-surface)] p-0 text-[var(--color-text)] motion-reduce:animate-none sm:max-w-[560px] sm:rounded-[var(--radius-frame)] sm:p-0"
        >
          <header className="shrink-0 border-b border-[var(--color-border)] px-5 pb-0 pt-5 sm:px-6 sm:pt-6">
            <div className="flex items-center gap-2.5 pr-10">
              <PexIcon name="help" tone="strong" size="lg" />
              <DialogTitle className="text-xl text-[var(--color-text)]">
                {t.title}
              </DialogTitle>
            </div>
            <DialogDescription className="mt-2 pr-8 text-sm text-[var(--color-text-muted)]">
              {t.intro}
            </DialogDescription>
            <div
              role="tablist"
              aria-label={t.title}
              className="mt-5 flex gap-6 overflow-x-auto"
            >
              {(
                ['new', 'own', ...(canAdmin ? ['admin' as const] : [])] as const
              ).map((value) => (
                <button
                  key={value}
                  id={`report-tab-${value}`}
                  role="tab"
                  aria-selected={tab === value}
                  aria-controls="report-tab-panel"
                  tabIndex={tab === value ? 0 : -1}
                  type="button"
                  onClick={() => setTab(value)}
                  onKeyDown={(event) => {
                    if (
                      !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(
                        event.key,
                      )
                    )
                      return
                    event.preventDefault()
                    const tabs = Array.from(
                      event.currentTarget.parentElement!.querySelectorAll<HTMLButtonElement>(
                        '[role="tab"]',
                      ),
                    )
                    const index = tabs.indexOf(event.currentTarget)
                    const next =
                      event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                          ? tabs.length - 1
                          : (index +
                              (event.key === 'ArrowRight' ? 1 : -1) +
                              tabs.length) %
                            tabs.length
                    tabs[next].click()
                    tabs[next].focus()
                  }}
                  className={`min-h-11 shrink-0 border-b-2 pb-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] ${tab === value ? 'border-[var(--color-text)] text-[var(--color-text)]' : 'border-transparent text-[var(--color-text-muted)]'}`}
                >
                  {value === 'new'
                    ? t.newReport
                    : value === 'own'
                      ? t.myReports
                      : t.admin}
                </button>
              ))}
            </div>
          </header>
          {children}
          <footer className="shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-5 pb-5 pt-4 sm:px-6">
            {footer}
          </footer>
          <DialogClose
            aria-label={t.close}
            className="absolute right-3 top-3 flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-control)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
          >
            <PexIcon name="close" size="sm" />
          </DialogClose>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
